import { useEffect, useRef } from 'react';

/**
 * 讓「原生橫向捲動容器」也能用垂直滾輪操作。
 *
 * 為什麼需要:overflowX: auto 的容器,瀏覽器原生只認 shift+滾輪 與觸控板的
 * 橫向手勢。一般滑鼠使用者滾輪往下滾,事件會直接冒泡給頁面,容器完全不動。
 *
 * 為什麼不能用 React 的 onWheel:React 17 之後 wheel 是掛在 root 上的
 * **passive** listener,在 onWheel 裡呼叫 preventDefault() 不會生效,還會在
 * console 噴警告。必須自己用 addEventListener 掛 { passive: false }。
 *
 * 邊界行為(重要):只有在容器「這個方向還捲得動」時才攔截。已經到最左/最右
 * 還繼續往同方向滾,就放手讓事件冒泡給頁面 —— 否則滑鼠停在排行榜上時整頁
 * 都滑不動,那是比原本更糟的體驗。
 *
 * 使用者本來就在做橫向手勢(deltaX ≠ 0,觸控板)時也不插手,交給瀏覽器原生處理。
 *
 * @returns ref —— 掛到那個 overflowX 容器上
 */
const useHorizontalWheelScroll = () => {
  const ref = useRef(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const onWheel = (e) => {
      // 觸控板的橫向手勢:瀏覽器原生已經處理得很好,不要重複介入
      if (e.deltaX !== 0) return;
      if (e.deltaY === 0) return;

      const max = el.scrollWidth - el.clientWidth;
      if (max <= 0) return; // 內容沒有溢出,沒得捲

      const atStart = el.scrollLeft <= 0;
      const atEnd = el.scrollLeft >= max - 1; // 留 1px 容差:縮放時 scrollLeft 會是小數

      // 到邊界還往同方向滾 → 把捲動還給頁面
      if ((e.deltaY < 0 && atStart) || (e.deltaY > 0 && atEnd)) return;

      e.preventDefault();
      el.scrollLeft += e.deltaY;
    };

    el.addEventListener('wheel', onWheel, { passive: false });
    return () => el.removeEventListener('wheel', onWheel);
  }, []);

  return ref;
};

export default useHorizontalWheelScroll;
